import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Button } from "design-system/atoms/Button"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { features, type Features } from "lib/table"
import { Pencil } from "lucide-react"
import { useGetBackofficeCustomersQuery } from "modules/backoffice/hooks/customers/useGetBackofficeCustomersQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { BackofficeGetCustomersResponseDataSchema } from "schema"
import { z } from "zod"
import { CustomersSortFields } from "."
import { BackofficeCustomerEditor } from "./BackofficeCustomerEditor"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof BackofficeGetCustomersResponseDataSchema>[number]
>()

type CustomersQueryReturn = ReturnType<typeof useGetBackofficeCustomersQuery>

type Props = {
  customers: CustomersQueryReturn["data"]
  isPending: CustomersQueryReturn["isPending"]
  fields: CustomersSortFields[]
  onSort: (field: CustomersSortFields) => void
  search: {
    field: CustomersSortFields
    order: "asc" | "desc"
  }
}

export const BackofficeCustomersTable = ({
  customers,
  isPending,
  fields,
  onSort,
  search,
}: Props) => {
  const { t } = useTranslation()
  const [customerId, setCustomerId] = useState<string | null>(null)

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("id", {
          header: t("backoffice.customers.table.id"),
        }),
        columnHelper.accessor("name", {
          header: t("backoffice.customers.table.name"),
          meta: { colSpan: 3 },
        }),
        columnHelper.accessor("phone", {
          header: t("backoffice.customers.table.phone"),
          meta: { colSpan: 3 },
        }),
        columnHelper.accessor("locale", {
          header: t("backoffice.customers.table.locale"),
          meta: { colSpan: 3 },
        }),
        columnHelper.display({
          id: "actions",
          cell: (props) => {
            const id = props.row.original.id

            return (
              <Button variant="outline" size="icon" onClick={() => setCustomerId(id)}>
                <Pencil />
              </Button>
            )
          },
          meta: {
            align: "right",
            className: "w-[60px]",
          },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: customers ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || customers === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (customers.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("backoffice.customers.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<
      Features,
      z.infer<typeof BackofficeGetCustomersResponseDataSchema>[number],
      unknown
    >,
  ) {
    if (!fields.includes(header.id as CustomersSortFields)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as CustomersSortFields),
    }
  }

  return (
    <>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  sortable={buildSortable(header)}
                  {...header.column.columnDef.meta}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
      {customerId && (
        <BackofficeCustomerEditor
          customerId={customerId}
          onOpenChange={() => setCustomerId(null)}
        />
      )}
    </>
  )
}
